import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { MapMeta } from '../types/map';
import { mapsApi } from '../services/api';
import { useAuth } from './AuthContext';

/**
 * Editor context state interface
 */
interface EditorContextType {
  isEditorMode: boolean;
  maps: MapMeta[];
  selectedMapId: string | null;
  selectedMap: MapMeta | null;
  isLoadingMaps: boolean;
  assetsVersion: number;
  enterEditorMode: () => void;
  exitEditorMode: () => void;
  refreshMaps: () => Promise<void>;
  refreshAssets: () => void;
  selectMap: (mapId: string | null) => void;
  updateSelectedMap: (updates: Partial<MapMeta>) => void;
}

const EditorContext = createContext<EditorContextType | null>(null);

interface EditorProviderProps {
  children: ReactNode;
}

/**
 * Editor provider component that manages editor mode state
 * Handles map list fetching and selection
 * @param children - Child components that will have access to editor context
 * @returns The editor provider wrapper component
 */
export function EditorProvider({ children }: EditorProviderProps): JSX.Element {
  const { isAuthenticated } = useAuth();
  const [isEditorMode, setIsEditorMode] = useState<boolean>(false);
  const [maps, setMaps] = useState<MapMeta[]>([]);
  const [selectedMapId, setSelectedMapId] = useState<string | null>(null);
  const [isLoadingMaps, setIsLoadingMaps] = useState<boolean>(false);
  const [assetsVersion, setAssetsVersion] = useState<number>(0);

  /**
   * Exit editor mode when user logs out
   */
  useEffect(() => {
    if (!isAuthenticated && isEditorMode) {
      setIsEditorMode(false);
      setSelectedMapId(null);
    }
  }, [isAuthenticated, isEditorMode]);

  /**
   * Fetches the list of available maps from the API
   */
  const refreshMaps = useCallback(async (): Promise<void> => {
    setIsLoadingMaps(true);
    try {
      const response = await mapsApi.list();
      setMaps(response.maps);
    } catch (err) {
      console.error('Failed to fetch maps:', err);
      setMaps([]);
    } finally {
      setIsLoadingMaps(false);
    }
  }, []);

  /**
   * Triggers a refresh of assets by incrementing the version counter
   * Components listening to assetsVersion will reload their assets
   */
  const refreshAssets = useCallback((): void => {
    setAssetsVersion((v) => v + 1);
  }, []);

  /**
   * Enters editor mode and loads the map list
   */
  const enterEditorMode = useCallback((): void => {
    setIsEditorMode(true);
  }, []);

  /**
   * Exits editor mode and clears selection
   */
  const exitEditorMode = useCallback((): void => {
    setIsEditorMode(false);
    setSelectedMapId(null);
  }, []);

  /**
   * Selects a map by ID
   * @param mapId - The map identifier to select, or null to deselect
   */
  const selectMap = useCallback((mapId: string | null): void => {
    setSelectedMapId(mapId);
  }, []);

  /**
   * Updates the selected map's metadata in local state
   * Used for real-time updates (e.g., background material change)
   */
  const updateSelectedMap = useCallback((updates: Partial<MapMeta>): void => {
    setMaps((prevMaps) =>
      prevMaps.map((map) =>
        map.id === selectedMapId ? { ...map, ...updates } : map
      )
    );
  }, [selectedMapId]);

  /**
   * Get the currently selected map object
   */
  const selectedMap = maps.find((m) => m.id === selectedMapId) || null;

  /**
   * Fetches maps when entering editor mode or when a map is selected
   */
  useEffect(() => {
    if (isEditorMode || selectedMapId) {
      refreshMaps();
    }
  }, [isEditorMode, selectedMapId, refreshMaps]);

  const value: EditorContextType = {
    isEditorMode,
    maps,
    selectedMapId,
    selectedMap,
    isLoadingMaps,
    assetsVersion,
    enterEditorMode,
    exitEditorMode,
    refreshMaps,
    refreshAssets,
    selectMap,
    updateSelectedMap
  };

  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  );
}

/**
 * Hook to access editor context
 * @returns The editor context value
 * @throws Error if used outside of EditorProvider
 */
export function useEditor(): EditorContextType {
  const context = useContext(EditorContext);

  if (!context) {
    throw new Error('useEditor must be used within an EditorProvider');
  }

  return context;
}

export default EditorContext;
